// src/components/filters/FilterBar.tsx
import React, { useState } from 'react';
import {FilterState} from '../../hooks/useFilters';
import SearchFilter from './SearchFilter';
import SpaceFilter from './SpaceFilter';
import DateRangeFilter from './DateRangeFilter';
import { ToggleButtonGroup, ToggleButton } from '@mui/material';

interface FilterBarProps {
  token: string | null;
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  enableDateFilter?: boolean; // New prop to control date filter enabled state
  enableSearchFilter?: boolean; // New prop to control search filter visibility
}

// Helper to get default fromDate (30 days ago) - must match useFilters default
const getDefaultFromDate = (): Date => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  date.setHours(0, 0, 0, 0);
  return date;
};

const FilterBar: React.FC<FilterBarProps> = ({ 
  token, 
  filters, 
  setFilters,
  enableDateFilter = true, // Default to enabling date filter
  enableSearchFilter = true // Default to enabling search filter
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Handler functions
  const setSearchFilter = (searchQuery: string | null) => {
    setFilters({ ...filters, searchQuery });
  };

  const setSpaceFilter = (spaceId: string | null) => {
    setFilters({ ...filters, spaceId });
  };

  const setDateRangeFilter = (fromDate: Date | null, toDate: Date | null) => {
    // Only update date filters if they're enabled
    if (enableDateFilter) {
      setFilters({ ...filters, fromDate, toDate });
    }
  };

  const setDateFilterType = (type: 'gathering' | 'submission') => {
    setFilters({ ...filters, dateFilterType: type });
  };

  const resetFilters = () => {
    // Reset to default state (30 days ago for fromDate)
    const defaultFromDate = getDefaultFromDate();
    if (enableDateFilter) {
      setFilters({ spaceId: null, dateFilterType: 'gathering', fromDate: defaultFromDate, toDate: null, searchQuery: null });
    } else {
      setFilters({ ...filters, spaceId: null, searchQuery: null });
    }
  };

  // Check if any filters differ from defaults
  // Note: fromDate is expected to be ~30 days ago by default, so we check if it's significantly different
  const isFromDateNonDefault = () => {
    if (!filters.fromDate) return false;
    const defaultDate = getDefaultFromDate();
    // Consider it "active" if more than 1 day different from default
    const diffMs = Math.abs(filters.fromDate.getTime() - defaultDate.getTime());
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    return diffDays > 1;
  };
  
  const hasActiveFilters = !!(
    filters.spaceId || 
    filters.searchQuery || 
    isFromDateNonDefault() || 
    filters.toDate
  );

  return (
    <div className="filter-bar">
      <div className="filter-bar-content">
        <div className="filter-bar-header" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: isExpanded ? '16px' : '0',
          minWidth: '800px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <h3 className="filter-bar-title" style={{ margin: 0 }}>
              Filters
              {hasActiveFilters && <span className="active-filters-indicator"> (Active)</span>}
            </h3>
            <button 
              className="toggle-filters-button"
              onClick={() => setIsExpanded(!isExpanded)}
              aria-label={isExpanded ? "Collapse filters" : "Expand filters"}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                padding: '4px',
                color: '#666',
                display: 'flex',
                alignItems: 'center',
                lineHeight: 1
              }}
            >
              {isExpanded ? '▼' : '▶'}
            </button>
          </div>
        </div>
        
        {isExpanded && (
          <>
            <div className="filter-components">
              <SpaceFilter
                token={token}
                selectedSpaceId={filters.spaceId}
                onSpaceChange={setSpaceFilter}
              />
              
              <div className={`date-filter-container ${!enableDateFilter ? 'disabled' : ''}`}>
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Date Range</div>
                  <ToggleButtonGroup
                    value={filters.dateFilterType}
                    exclusive
                    onChange={(_, newValue) => {
                      if (newValue !== null) {
                        setDateFilterType(newValue);
                      }
                    }}
                    disabled={!enableDateFilter}
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        border: '1px solid #ddd',
                        '&.Mui-selected': {
                          backgroundColor: '#1976d2',
                          color: 'white',
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: '#1565c0',
                          },
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        },
                      },
                    }}
                  >
                    <ToggleButton value="gathering">
                      Gathering Date
                    </ToggleButton>
                    <ToggleButton value="submission">
                      Submission Date
                    </ToggleButton>
                  </ToggleButtonGroup>
                </div>
                
                <DateRangeFilter
                  label=""
                  fromDate={filters.fromDate}
                  toDate={filters.toDate}
                  onDateRangeChange={setDateRangeFilter}
                  disabled={!enableDateFilter}
                  minDate={getDefaultFromDate()}
                />
                {!enableDateFilter && (
                  <div className="filter-disabled-overlay">
                    <span className="disabled-message">Date filters not available in calendar views</span>
                  </div>
                )}
              </div>
            </div>
            
            {enableSearchFilter && (
              <div className="search-filter-row">
                <SearchFilter
                  searchQuery={filters.searchQuery}
                  onSearchChange={setSearchFilter}
                />
              </div>
            )}
            
            <div className="filter-actions-row">
              <button 
                className="reset-filters-button"
                onClick={resetFilters}
                disabled={
                  !filters.spaceId && 
                  (!enableSearchFilter || !filters.searchQuery) && 
                  (!enableDateFilter || (!filters.fromDate && !filters.toDate))
                }
              >
                Reset All Filters
              </button>
              
              <a 
                href="#/gatherings/past" 
                className="past-gatherings-link"
              >
                Search past gatherings →
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FilterBar;